



'use client';
import { API_SERVER } from 'config/constant';
import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Award,
  Zap,
  Target,
  // Star,
  ChevronRight,
  Sparkles,
  ChevronLeft,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Buttons } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useColor } from '../../../../components/ui/ColorContext';
import ColorPicker from '../../../../components/ui/ColorPicker';
import { CustomCoinIcon } from '../../../../components/ui/CustomIcons';
import { ScenarioLearnerScore } from 'utils/gameApplication/gamePlayService';

import { CustomMedalIcon } from '../../../../components/ui/CustomIcons';
import { ScoreContext } from '../GamePreview';
import { createOrUpdateStoryBlock } from '../common';

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 255, 187';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
};

const playedBadgeSoundKeys = new Set<string>();

const NavigationArrow: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
}> = ({ direction, onClick }) => {
  const { primaryColor } = useColor();
  return (
    <Buttons
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="h-10 w-[3.75rem] rounded-3xl px-4 py-2 transition-all duration-200 hover:scale-110"
      style={{
        background: `linear-gradient(${
          direction === 'left' ? '275.041deg' : '90deg'
        },
                rgba(${hexToRgb(primaryColor)}, 0.6) 7.25%, rgba(${hexToRgb(
          primaryColor,
        )}, 0.15) 84.803%)`,
        border: `1px solid ${primaryColor}`,
        boxShadow: `0px 0px 15.9542px 0px rgba(${hexToRgb(primaryColor)}, 0.3)`,
      }}
    >
      {direction === 'left' ? (
        <ChevronLeft className="!h-4 !w-4 text-white" />
      ) : (
        <ChevronRight className="!h-4 !w-4 text-white" />
      )}
    </Buttons>
  );
};

interface RewardCardProps {
  title: string;
  value: string | number;
  icon: 'achievement' | 'progress';
  subtitle?: string;
}
// Enhanced RewardCard with new icons and better design

interface StarsCardProps {
  filledStars: number[]; // [1, 0.35, 0]
  totalStars: number;
  visible?: boolean;
  animationDelay?: number;
  onStarIgnite?: () => void;
}


type StarProps = {
  type: 'full' | 'partial' | 'empty';
  fillPercentage: number;
  index: number;
  shouldAnimate: boolean;
  pulseComplete: boolean;
  primaryColor: string;
};


const normalizeStarValue = (value: number): number => {
  const clamped = Math.max(0, Math.min(1, value));
  if (clamped >= 0.999) return 1;
  if (clamped <= 0.001) return 0;
  return clamped;
};

const Star = ({
  type,
  fillPercentage,
  index,
  shouldAnimate,
  primaryColor,
}: StarProps) => {
  const isLit = type !== 'empty';

  return (
    <div className="scenario-aura-wrapper">
      <div
        className={`starburst ${
          type === 'full'
            ? 'filled'
            : type === 'partial'
            ? 'partial'
            : 'empty'
        } ${isLit && shouldAnimate ? 'star-pop' : ''}`}
          data-fill={fillPercentage}
        style={
          {
            ['--fillPct' as any]: `${Math.max(
              0,
              Math.min(1, fillPercentage),
            ) * 100}%`,
          } as React.CSSProperties
        }
      >
        <svg viewBox="0 0 24 24" className="h-full w-full">
          <defs>
            <linearGradient id={`plasmaGrad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={`rgba(${hexToRgb(primaryColor)}, 1)`} />
              <stop offset="50%" stopColor={`rgba(${hexToRgb(primaryColor)}, 0.8)`} />
              <stop offset="100%" stopColor={`rgba(${hexToRgb(primaryColor)}, 0.6)`} />
            </linearGradient>

            {/* ✅ Partial fill */}
            {/* <linearGradient id={`fillMask-${index}`}> */}
            <linearGradient
  id={`fillMask-${index}`}
  x1="0%"
  y1="0%"
  x2="100%"
  y2="0%"
>
              <stop offset={`${fillPercentage * 100}%`} stopColor="white" />
              <stop offset={`${fillPercentage * 100}%`} stopColor="black" />
            </linearGradient>

            <mask id={`starMask-${index}`}>
              <rect width="24" height="24" fill={`url(#fillMask-${index})`} />
            </mask>
          </defs>

          {type !== 'empty' ? (
            <g className="star-fill">
              <polygon
                points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
                fill={`url(#plasmaGrad-${index})`}
                // mask={`url(#starMask-${index})`}
                 mask={type === 'full' ? undefined : `url(#starMask-${index})`}
                stroke={`rgba(${hexToRgb(primaryColor)}, 1)`}
                strokeWidth="1.2"
              />
              <circle cx="12" cy="12" r="2.4" fill="rgba(255,255,255,0.95)" />
            </g>
          ) : (
            <g className="star-empty">
              <polygon
                points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
                fill="transparent"
                stroke={`rgba(${hexToRgb(primaryColor)}, 0.4)`}
                strokeWidth="1"
              />
            </g>
          )}
        </svg>
      </div>

      {isLit && (
        <svg className="pointer-events-none absolute" style={{
          top: '-4px',
          left: '-4px',
          width: 'calc(100% + 12px)',
          height: 'calc(100% + 12px)',
        }} viewBox="0 0 40 40">
          <circle
            cx="20"
            cy="20"
            r="17"
            fill="none"
            stroke={primaryColor}
            strokeOpacity={0.9}
            strokeWidth="1.2"
            strokeDasharray="12 24"
            strokeDashoffset="6"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 20 20"
              to="360 20 20"
              dur="4s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      )}
    </div>
  );
};
export const StarsCard = ({
  filledStars,
  totalStars,
  visible = true,
  animationDelay = 0,
  onStarIgnite,
}: StarsCardProps) => {
  const [litStars, setLitStars] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [animatedStars, setAnimatedStars] = useState<Set<number>>(new Set());
  const [completedPulseAnimations, setCompletedPulseAnimations] = useState<
    Set<number>
  >(new Set());

  const filledStarsKey = filledStars.join('|');

  useEffect(() => {
    if (!visible) return;

    setLitStars(0);
    setShowRating(false);
    setAnimatedStars(new Set());
    setCompletedPulseAnimations(new Set());

    const timeouts: number[] = [];

    filledStars.forEach((fill, i) => {
      if (fill > 0) {
        timeouts.push(
          window.setTimeout(() => {
            setLitStars((prev) => Math.max(prev, i + 1));
            setAnimatedStars((prev) => new Set(prev).add(i));
            onStarIgnite?.();
          }, animationDelay + i * 400),
        );

        timeouts.push(
          window.setTimeout(() => {
            setCompletedPulseAnimations((prev) => new Set(prev).add(i));
          }, animationDelay + i * 400 + 700),
        );
      }
    });

    timeouts.push(
      window.setTimeout(() => {
        setShowRating(true);
      }, animationDelay + filledStars.length * 400 + 200),
    );

    return () => timeouts.forEach(clearTimeout);
  }, [visible, filledStarsKey, animationDelay]);

  const rating = getRating(filledStars.filter((v) => v === 1).length);
  const { primaryColor } = useColor();

  return (
    <>
      <span className="scenario-card-label">Stars</span>

      <div className="card-content">
        <div className="stars">
        
          {Array.from({ length: totalStars }).map((_, i) => {
  const fill = filledStars[i] ?? 0;

  return (
    <Star
      key={i}
      fillPercentage={fill}
      index={i}
      shouldAnimate={animatedStars.has(i)}
      pulseComplete={completedPulseAnimations.has(i)}
      primaryColor={primaryColor}
      type={
        fill === 1
          ? 'full'
          : fill > 0
          ? 'partial'
          : 'empty'
      }
    />
  );
})}
          {/* </div> */}
        </div>
      </div>
    </>
  );
};

const getRating = (stars: number): string => {
  switch (stars) {
    case 1:
      return 'DEVELOPING';
    case 2:
      return 'STRONG';
    case 3:
      return 'EXCELLENT';
    default:
      return '';
  }
};

interface ScenarioCompletionProps {
  gameProgressData?:any;
  setGameProgressData?:any;
   BlockSeqNextPreviousNotes: (inputId?:any,questno?:any) => void;
  setShowScenarioLeaderboard: any;
  badgeImage: any;
  badgename: any;
  setisScreenshot?: any;
  isScreenshot?: any;
  getFeedbackData?: any;
  setFirstLoading?: any;
  FeedbackcurrentPosition?: any;
  setFeedbackCurrentPosition?: any;
  learner_game_play_id: any;
  backgroundScreenUrl?: string;
  preloadedAssets?: any;
  currentScreenId?: number;
  handleScenarioReplayButtonClick: any;
  getData: any;
  setCurrentQuestNo: any;
  gameInfo?: any;
  profile?: any;
  scoreLoaded?: any;
  setCurrentScreenId: any;
  NonPlayerNameLanguage: any;
  getSelectedOptions: any;
  sceneriowiseEarnedScore: any;
  sceneriowiseHighestEarnedScore: any;
  scenarioTotalScore: any;
  scenarioAllTimeScore: any;
  data: any;
  LastModiPrevData: any;
  getNoteNextData: any;
  score: number;
  pointsEarned: number;
  stars: number;
  totalInteractions: number;
  currentInteraction: number;
  competencyLevel: number;
  newCompetencyLevel: number;
  scenarioType: 'basic' | 'complex';

    scenariowisebestscore1:any;
  onContinue?: () => void;
  minPassingScore: number;
  getNoteNextDataone: any;
  // AllLearnerLearderboard: any;
  setAllLearnerScenarioScore: any;
  allLearnerScenarioScore: any;
}

const getMessageByPerformance = (score: number, stars: number): string => {
  if (score >= 90) return "Outstanding empathy! You've mastered this scenario.";
  if (score >= 80) return 'Excellent work! Strong empathetic response.';
  if (score >= 70) return "Great job! You're building empathy skills.";
  if (score >= 60) return 'Good progress. Keep practicing empathy.';
  return 'Keep practicing to improve your empathy skills.';
};

const ScenarioCompletionFutureTheme: React.FC<ScenarioCompletionProps> = ({
  scoreLoaded,
  setGameProgressData,
    gameProgressData,
  BlockSeqNextPreviousNotes,
  setShowScenarioLeaderboard,
  badgeImage,
  badgename,
  setFirstLoading,
  isScreenshot,
  setisScreenshot,
  gameInfo,
  profile,
  FeedbackcurrentPosition,
  setFeedbackCurrentPosition,
  backgroundScreenUrl,
  NonPlayerNameLanguage,
  getFeedbackData,
  preloadedAssets,
  getSelectedOptions,
  currentScreenId,
    scenariowisebestscore1,
  learner_game_play_id,

  getData,
  sceneriowiseEarnedScore,
  sceneriowiseHighestEarnedScore,
  scenarioTotalScore,
  scenarioAllTimeScore,

  LastModiPrevData,
  score,

  stars,

  currentInteraction,
  competencyLevel,
  newCompetencyLevel,
  scenarioType,

  minPassingScore,
  getNoteNextDataone,
  // AllLearnerLearderboard,
  setAllLearnerScenarioScore,
  allLearnerScenarioScore,
  handleScenarioReplayButtonClick,

  setCurrentQuestNo,
  setCurrentScreenId,
  data,
  pointsEarned,
  totalInteractions,

  onContinue,
}) => {
  // by jaswanth

const { setlearnerActivityrec ,deviceInfo} = useContext(ScoreContext);
    // by claude
  // Add this ref near your other refs
  const animationStarted = useRef(false);
  // const panelAppearPlayed = useRef(false);
  //const panelVisibleRef = useRef(false);
//kaviya
const [sequenceStep, setSequenceStep] = useState(0);
const [isSequenceComplete, setIsSequenceComplete] = useState(false);
  const panelAppearPlayed = useRef(false);;
const playedBadgeSoundKeys = new Set<string>();
  const panelVisibleRef = useRef(false);



  // Add this NEW useEffect — separate from the initialization one
  useEffect(() => {
    // Wait until we have a real score value
    if (!sceneriowiseEarnedScore || sceneriowiseEarnedScore <= 0) return;

    // Prevent running animation twice
    if (animationStarted.current) return;
    animationStarted.current = true;

    // Update the data-target with the real value before animation runs
    const scoreEl = document.querySelector('.score-value') as HTMLElement;
    if (scoreEl) {
      scoreEl.setAttribute('data-target', String(sceneriowiseEarnedScore));
      scoreEl.setAttribute('data-max', String(scenarioTotalScore));
    }

  }, [sceneriowiseEarnedScore, scenarioTotalScore]);
  // by claude
const audioCtxRef = useRef<AudioContext | null>(null);
  const initialized = useRef(false);
function playMenuAndNext(ctx: AudioContext) {
  const now = ctx.currentTime;
  [520, 780].forEach((freq, i) => {
    const t = now + i * 0.06;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.15, t + 0.005);
    gain.gain.linearRampToValueAtTime(0, t + 0.05);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.06);
  });
}
function getAudioContext() {
  if (!audioCtxRef.current) {
    audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtxRef.current.state === 'suspended') {
    audioCtxRef.current.resume();
  }
  return audioCtxRef.current;
}

function playMenuSound() {
  const ctx = getAudioContext();
  playMenuAndNext(ctx);
}


const scoreRef = useRef(sceneriowiseEarnedScore);
useEffect(() => {
  scoreRef.current = sceneriowiseEarnedScore;
}, [sceneriowiseEarnedScore]);

  useEffect(() => {
    //  if (!sceneriowiseEarnedScore === null || sceneriowiseEarnedScore === undefined) return;
    if (sceneriowiseEarnedScore === null || sceneriowiseEarnedScore === undefined) return;
     const blockdata ={
        blockId: data.blockId,
        blockGameId: data.blockGameId,
        blockQuestNo: data.blockQuestNo,
        blockScenario: data.blockScenario,
        blockSecondaryId: data.blockSecondaryId,
        blockPrimarySequence: data.blockPrimarySequence,
        blockDragSequence: data.blockDragSequence,
        blockShowNavigate: data.blockShowNavigate,
        blockLeadTo: data.blockLeadTo,
        blockChoosen: data.blockChoosen,
        storyscreentype:"ScenarioCompletion"
      }
    setlearnerActivityrec((prev:any)=>
      createOrUpdateStoryBlock(
        prev,
        {
          ...blockdata,
          sceneriowiseEarnedScore:sceneriowiseEarnedScore,
          scenarioTotalScore:scenarioTotalScore,
        },
        deviceInfo
      )
    );
    if (initialized.current) return;
    initialized.current = true;

    // Audio context and helpers
    let __audioCtx: AudioContext | null = null;
    let __audioArmed = false;
    let __animationInProgress = false;
    let __animationSkipped = false;

    function getAudioContext() {
      if (!__audioCtx)
        __audioCtx = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      if (__audioCtx.state === 'suspended') __audioCtx.resume();
      return __audioCtx;
    }

    // Descending beep for star reveals (user-provided)
    function playStarBeep() {
      if (!__audioArmed) return;
      const ctx = getAudioContext();
      const notes = [880, 659.25, 523.25]; // A5, E5, C5
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const startTime = ctx.currentTime + i * 0.12;
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.08, startTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
        osc.start(startTime);
        osc.stop(startTime + 0.3);
      });
    }

    function playScoreTick(progress01 = 0) {
      if (!__audioArmed) return;
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      const base = 420;
      const top = 980;
      const freq = base + (top - base) * Math.max(0, Math.min(1, progress01));

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.08, now + 0.03);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.09, now + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.07);
    }

    // Victory Fanfare 2.0 - Ultimate game completion sound
    function playPanelAppear() {
      if (!__audioArmed) return;
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // Layer 1: Resonant bass drop with pitch rise (impact)
      const bassOsc = ctx.createOscillator();
      const bassGain = ctx.createGain();
      const bassFilter = ctx.createBiquadFilter();
      bassOsc.type = 'sine';
      bassOsc.frequency.setValueAtTime(40, now);
      bassOsc.frequency.exponentialRampToValueAtTime(80, now + 0.35);
      bassFilter.type = 'lowpass';
      bassFilter.frequency.value = 150;
      bassFilter.Q.value = 5;
      bassGain.gain.setValueAtTime(0, now);
      bassGain.gain.linearRampToValueAtTime(0.35, now + 0.05);
      bassGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      bassOsc.connect(bassFilter);
      bassFilter.connect(bassGain);
      bassGain.connect(ctx.destination);
      bassOsc.start(now);
      bassOsc.stop(now + 0.45);

      // Layer 2: Ascending arpeggio (C5 → E5 → G5 → C6) - achievement unlock feel
      const arpNotes = [523.25, 659.25, 783.99, 1046.5];
      arpNotes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        const startTime = now + 0.05 + i * 0.06;
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.12 - i * 0.02, startTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + 0.3);
      });

      // Layer 3: Harmonic shimmer with vibrato (sparkle texture)
      const shimmerOsc = ctx.createOscillator();
      const shimmerGain = ctx.createGain();
      const vibratoLfo = ctx.createOscillator();
      const vibratoGain = ctx.createGain();
      shimmerOsc.type = 'triangle';
      shimmerOsc.frequency.setValueAtTime(2400, now + 0.15);
      shimmerOsc.frequency.exponentialRampToValueAtTime(4000, now + 0.6);
      vibratoLfo.frequency.value = 18;
      vibratoGain.gain.value = 150;
      vibratoLfo.connect(vibratoGain);
      vibratoGain.connect(shimmerOsc.frequency);
      shimmerGain.gain.setValueAtTime(0, now + 0.15);
      shimmerGain.gain.linearRampToValueAtTime(0.05, now + 0.22);
      shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
      shimmerOsc.connect(shimmerGain);
      shimmerGain.connect(ctx.destination);
      vibratoLfo.start(now + 0.15);
      shimmerOsc.start(now + 0.15);
      vibratoLfo.stop(now + 0.85);
      shimmerOsc.stop(now + 0.85);

      // Layer 4: Synth pad swell (filtered sawtooth chord)
      const padNotes = [261.63, 329.63, 392.0]; // C4, E4, G4
      padNotes.forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        osc.type = 'sawtooth';
        osc.frequency.value = freq;
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(400, now + 0.1);
        filter.frequency.exponentialRampToValueAtTime(2000, now + 0.5);
        filter.Q.value = 2;
        gain.gain.setValueAtTime(0, now + 0.1);
        gain.gain.linearRampToValueAtTime(0.04, now + 0.4);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + 0.1);
        osc.stop(now + 1.05);
      });

      // Layer 5: Final bright "ding" punctuation
      const dingOsc = ctx.createOscillator();
      const dingGain = ctx.createGain();
      dingOsc.type = 'sine';
      dingOsc.frequency.setValueAtTime(1568, now + 0.4); // G6
      dingGain.gain.setValueAtTime(0, now + 0.4);
      dingGain.gain.linearRampToValueAtTime(0.15, now + 0.42);
      dingGain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
      dingOsc.connect(dingGain);
      dingGain.connect(ctx.destination);
      dingOsc.start(now + 0.4);
      dingOsc.stop(now + 0.75);
    }

    const scenarioBody = document.querySelector(
      '.scenario-body',
    ) as HTMLElement;

    // Confetti burst effect
    function spawnConfetti() {
      const canvas = document.createElement('canvas');
      canvas.style.cssText =
        'position:fixed;inset:0;pointer-events:none;z-index:999;';
      scenarioBody?.appendChild(canvas);

      const ctx2d = canvas.getContext('2d');
      if (!ctx2d) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const particles: Array<{
        x: number;
        y: number;
        vx: number;
        vy: number;
        size: number;
        color: string;
        alpha: number;
        rotation: number;
        rotationSpeed: number;
        gravity: number;
      }> = [];
      // Get confetti colors from CSS variables for theme consistency
      const rootStyles = getComputedStyle(document.documentElement);
      const baseCyan =
        rootStyles.getPropertyValue('--cyan').trim() || '#00f2ff';
      const cyanLight =
        rootStyles.getPropertyValue('--cyan-light').trim() || '#80ffff';
      const cyanMid =
        rootStyles.getPropertyValue('--cyan-mid').trim() || '#00d4ff';
      const cyanBlue =
        rootStyles.getPropertyValue('--cyan-blue').trim() || '#00b8ff';
      const colors = [baseCyan, cyanMid, cyanLight, '#ffffff', cyanBlue];

      for (let i = 0; i < 100; i++) {
        const angle = (Math.PI * 2 * i) / 100 + Math.random() * 0.5;
        const velocity = 10 + Math.random() * 15;
        particles.push({
          x: canvas.width / 2,
          y: canvas.height / 2,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity - 5,
          size: 5 + Math.random() * 8,
          color: colors[Math.floor(Math.random() * colors.length)],
          alpha: 1,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.4,
          gravity: 0.18,
        });
      }

      function animate() {
        ctx2d!.clearRect(0, 0, canvas.width, canvas.height);
        let alive = false;

        particles.forEach((p) => {
          if (p.alpha <= 0) return;
          alive = true;

          p.x += p.vx;
          p.y += p.vy;
          p.vy += p.gravity;
          p.vx *= 0.99;
          p.rotation += p.rotationSpeed;
          p.alpha -= 0.012;

          ctx2d!.save();
          ctx2d!.translate(p.x, p.y);
          ctx2d!.rotate(p.rotation);
          ctx2d!.globalAlpha = Math.max(0, p.alpha);
          ctx2d!.fillStyle = p.color;
          ctx2d!.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
          ctx2d!.restore();
        });

        if (alive) requestAnimationFrame(animate);
        else canvas.remove();
      }

      animate();
    }

    // Badge reveal chime sound
    function playBadgeReveal() {
      if (!__audioArmed) return;
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // First tone
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now);
      gain1.gain.setValueAtTime(0.0001, now);
      gain1.gain.exponentialRampToValueAtTime(0.14, now + 0.02);
      gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.35);

      // Second tone (fifth above)
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1320, now + 0.05);
      gain2.gain.setValueAtTime(0.0001, now);
      gain2.gain.exponentialRampToValueAtTime(0.12, now + 0.07);
      gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.05);
      osc2.stop(now + 0.4);
    }

    // Progress meter fill - series of ascending pings
    function playMeterFill() {
      if (!__audioArmed) return;
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // Series of ascending pings (like a progress indicator)
      const steps = 8;
      for (let i = 0; i < steps; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        const freq = 300 + i * 70; // Rising: 300, 370, 440, 510, 580, 650, 720, 790
        const startTime = now + i * 0.06; // Quick succession

        osc.frequency.setValueAtTime(freq, startTime);

        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.12);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + 0.15);
      }
    }

    function flashScene(alpha = 0.32) {
      document.documentElement.style.setProperty('--flashAlpha', String(alpha));
      scenarioBody?.classList.remove('flash-on');
      void scenarioBody?.offsetWidth;
      scenarioBody?.classList.add('flash-on');
      setTimeout(() => scenarioBody?.classList.remove('flash-on'), 220);
    }

    function microShakePanel(strength = 1) {
      const panel = document.querySelector('.scenario-panel') as HTMLElement;
      if (!panel || !panel.animate) return;
      const s = Math.max(0.6, Math.min(1.6, strength));
      panel.animate(
        [
          { transform: 'translate(0, 0)' },
          { transform: `translate(${1.2 * s}px, ${-0.8 * s}px)` },
          { transform: `translate(${-1.4 * s}px, ${1.0 * s}px)` },
          { transform: `translate(${1.0 * s}px, ${0.8 * s}px)` },
          { transform: 'translate(0, 0)' },
        ],
        {
          duration: 220,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        },
      );
    }

    function spawnStarPulse(wrapper: Element, intensity = 1) {
      if (!wrapper) return;
      const pulse = document.createElement('div');
      pulse.className = 'star-pulse';
      pulse.style.setProperty(
        '--pulseAlpha',
        String(Math.max(0.25, Math.min(1, intensity))),
      );
      wrapper.appendChild(pulse);
      pulse.addEventListener('animationend', () => pulse.remove());
    }

    function ensureStarLayers(starburst: Element) {
      const hasEmpty = !!starburst.querySelector('svg.star-empty');
      const hasFill = !!starburst.querySelector('svg.star-fill');

      const emptySvg = `
        <svg class="star-empty" viewBox="0 0 24 24" aria-hidden="true">
          <polygon class="star-body" points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" />
          <circle class="star-glow" cx="12" cy="12" r="2" />
        </svg>`;

      const fillSvg = `
        <svg class="star-fill" viewBox="0 0 24 24" aria-hidden="true">
          <polygon class="star-body" points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" />
          <circle class="star-glow" cx="12" cy="12" r="2" />
        </svg>`;

      if (!hasEmpty) {
        starburst.insertAdjacentHTML('afterbegin', emptySvg);
      }
      if (!hasFill) {
        starburst.insertAdjacentHTML('beforeend', fillSvg);
      }
    }

    function getStarFillPct(starburst: Element) {
      if (starburst.classList.contains('filled')) return '100%';
      if (starburst.classList.contains('partial')) {
        const raw = starburst.getAttribute('data-fill');
        if (raw != null && raw !== '') {
          const v = Math.max(0, Math.min(1, Number(raw)));
          return `${v * 100}%`;
        }
        const cssVar = getComputedStyle(starburst)
          .getPropertyValue('--fillPct')
          .trim();
        return cssVar || '50%';
      }
      return '0%';
    }

    // Helper delay function
    function delay(ms: number): Promise<void> {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    function runStarsSequence({
      withSound,
    }: {
      withSound: boolean;
    }): Promise<void> {
      return new Promise(async (resolve) => {
        const wrappers = Array.from(
          document.querySelectorAll('.stars .scenario-aura-wrapper'),
        );

        wrappers.forEach((wrapper) => {
          const starburst = wrapper.querySelector('.starburst') as HTMLElement;
          if (!starburst) return;

          ensureStarLayers(starburst);

          const fillPct = getStarFillPct(starburst);
          starburst.style.setProperty('--fillPct', fillPct);

          starburst.classList.remove('reveal');
          const ring = wrapper.querySelector('.aura-ring');
          if (ring) ring.classList.remove('ring-on');
        });

        for (let idx = 0; idx < wrappers.length; idx++) {
          const wrapper = wrappers[idx];
          const starburst = wrapper.querySelector('.starburst') as HTMLElement;
          if (!starburst) continue;

          const isOn =
            starburst.classList.contains('filled') ||
            starburst.classList.contains('partial');
          const pct = getStarFillPct(starburst);
          const intensity = Math.max(
            0.25,
            Math.min(1, parseFloat(pct) / 100 || 1),
          );

          // Play sound BEFORE revealing so they happen together
          if (isOn && withSound) {
            playStarBeep();
          }

          starburst.classList.add('reveal');

          if (isOn) {
            spawnStarPulse(wrapper, intensity);

            starburst.style.setProperty('--fillPct', '0%');
            requestAnimationFrame(() => {
              starburst.style.setProperty('--fillPct', pct);
            });

            let ring = wrapper.querySelector('.aura-ring');
            if (!ring) {
              ring = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'svg',
              );
              ring.setAttribute('class', 'aura-ring');
              ring.setAttribute('viewBox', '0 0 24 24');
              ring.innerHTML = `<circle cx="12" cy="12" r="10"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="4s" repeatCount="indefinite"/></circle>`;
              wrapper.appendChild(ring);
            }
            ring.classList.add('ring-on');

            if (idx === wrappers.length - 1) {
              flashScene(0.22);
              microShakePanel(1);
            }
          }

          await delay(300); // Wait between stars
        }

        resolve();
      });
    }

    function runScoreCount({
      withSound,
      totalDurationMs = 600,
    }: {
      withSound: boolean;
      totalDurationMs?: number;
    }): Promise<void> {
      return new Promise((resolve) => {
        const el = document.querySelector('.score-value') as HTMLElement;
        if (!el) {
          resolve();
          return;
        }

        // Read target from data attribute
        // const targetVal = Number(el.getAttribute('data-target')) ;
        const targetVal = sceneriowiseEarnedScore;
        const max = el.getAttribute('data-max');

        scenarioBody?.classList.add('score-ramp');
        flashScene(0.18);

        el.classList.remove('score-reveal');
        void el.offsetWidth;
        el.classList.add('score-reveal');

        const start = performance.now();
        const startVal = 0;
        const endVal = targetVal;
        const steps = Math.max(8, Math.min(24, Math.round(endVal / 12) || 12));
        let lastStep = -1;

        function easeOutCubic(t: number) {
          return 1 - Math.pow(1 - t, 3);
        }

        function frame(now: number) {
          const t = Math.max(0, Math.min(1, (now - start) / totalDurationMs));
          const eased = easeOutCubic(t);
          const val = Math.round(startVal + (endVal - startVal) * eased);
          el.textContent = `${val}/${max}`;

          const step = Math.floor(eased * steps);
          if (withSound && step !== lastStep) {
            lastStep = step;
            playScoreTick(eased);
          }

          if (t < 1) {
            requestAnimationFrame(frame);
          } else {
            el.textContent = `${endVal}/${max}`;
            scenarioBody?.classList.remove('score-ramp');
            flashScene(0.14);
            microShakePanel(0.8);
            resolve();
          }
        }

        requestAnimationFrame(frame);
      });
    }

    // function runMeterAnimation({ withSound }: { withSound: boolean }): Promise<void> {
    //   return new Promise((resolve) => {
    //     const meter = document.querySelector('.meter-fill') as HTMLElement;
    //     const pct = document.querySelector('.meter-percentage') as HTMLElement;
    //     if (!meter) { resolve(); return; }
    //     if (withSound) playMeterFill();
    //     meter.classList.add('meter-animate');
    //     // Show percentage after slight delay
    //     setTimeout(() => {
    //       if (pct) pct.classList.add('visible');
    //     }, 200);
    //     setTimeout(resolve, 500); // Match meter transition duration
    //   });
    // }

    function runMeterAnimation({
      withSound,
    }: {
      withSound: boolean;
    }): Promise<void> {
      return new Promise((resolve) => {
        const meter = document.querySelector('.meter-fill') as HTMLElement;
        const pct = document.querySelector('.meter-percentage') as HTMLElement;
        if (!meter) {
          resolve();
          return;
        }

        // Read actual percentage from data attribute
        const pctValue = meter.getAttribute('data-pct') || '70';
        meter.style.setProperty('--meter-pct', `${pctValue}%`);

        if (withSound) playMeterFill();
        meter.classList.add('meter-animate');

        setTimeout(() => {
          if (pct) pct.classList.add('visible');
        }, 200);
        setTimeout(resolve, 500);
      });
    }

    function runStatusReveal({
      withSound,
    }: {
      withSound: boolean;
    }): Promise<void> {
      return new Promise((resolve) => {
        const badge = document.querySelector(
          '.status-badge-container',
        ) as HTMLElement;
        if (badge) badge.classList.add('badge-reveal');
        const badgeKey = `${data?.blockScenario ?? 'unknown'}|${sceneriowiseEarnedScore}|${scenarioTotalScore}|badge`;
        if (
          withSound &&
          panelVisibleRef.current &&
          !playedBadgeSoundKeys.has(badgeKey)
        ) {
          window.setTimeout(() => {
            playBadgeReveal();
          }, 350);
          playedBadgeSoundKeys.add(badgeKey);
        }
        setTimeout(resolve, 400); // Match badge reveal duration
      });
    }

    function animateCard(cardSelector: string): Promise<void> {
      return new Promise((resolve) => {
        const card = document.querySelector(cardSelector) as HTMLElement;
        if (!card) {
          resolve();
          return;
        }
        card.classList.add('card-animate');
        setTimeout(resolve, 400); // Match cardBoot duration
      });
    }

    function animateContent(contentSelector: string): Promise<void> {
      return new Promise((resolve) => {
        const content = document.querySelector(contentSelector) as HTMLElement;
        if (!content) {
          resolve();
          return;
        }
        content.classList.add('content-animate');
        setTimeout(resolve, 300); // Match content reveal duration
      });
    }

    function skipToComplete() {
      if (!__animationInProgress) return; // No effect if not animating

      __animationSkipped = true;
      __audioArmed = false; // Stop sounds

      // Force all cards to appear
      document.querySelectorAll('.scenario-card').forEach((card) => {
        card.classList.add('card-animate');
        const content = card.querySelector('.card-content');
        if (content) content.classList.add('content-animate');
      });

      // Force competency section
      const compSection = document.querySelector('.competency-section');
      if (compSection) compSection.classList.add('card-animate');

      // Force score to final value
      const scoreEl = document.querySelector('.score-value') as HTMLElement;
      if (scoreEl) {
        const target = scoreEl.getAttribute('data-target');
        const max = scoreEl.getAttribute('data-max');
        scoreEl.textContent = `${target}/${max}`;
        scoreEl.classList.add('score-reveal');
      }

      // Force all stars to reveal with proper fill
      document.querySelectorAll('.starburst').forEach((star) => {
        const starburst = star as HTMLElement;
        ensureStarLayers(starburst);
        const fillPct = getStarFillPct(starburst);
        starburst.style.setProperty('--fillPct', fillPct);
        starburst.classList.add('reveal');
      });
      document.querySelectorAll('.aura-ring').forEach((ring) => {
        ring.classList.add('ring-on');
      });

      // Force badge reveal
      const badge = document.querySelector('.status-badge-container');
      if (badge) badge.classList.add('badge-reveal');

      // Force meter fill
      const meterFill = document.querySelector('.meter-fill');
      if (meterFill) meterFill.classList.add('meter-animate');
      const meterPct = document.querySelector('.meter-percentage');
      if (meterPct) meterPct.classList.add('visible');

      scenarioBody?.classList.remove('run-anim', 'score-ramp');
      __animationInProgress = false;
    }

   
async function startSequence({ withSound }: { withSound: boolean }) {
  __animationInProgress = true;
  __animationSkipped = false;
      panelVisibleRef.current = false;

  scenarioBody?.classList.add('run-anim');

  // Panel entry
  const panelWrapper = document.querySelector(
    '.border-animation-wrapper',
  ) as HTMLElement;

  if (panelWrapper) {
    panelWrapper.classList.add('panel-visible');
        panelVisibleRef.current = true;
  }

      // Play celebratory sound and confetti at the very start
      await delay(600);
      if (__animationSkipped) return;
   

      // Play celebratory sound only after the panel is visible, and only once
      if (withSound && panelVisibleRef.current && !panelAppearPlayed.current) {
        panelAppearPlayed.current = true;
      
        playPanelAppear();
        spawnConfetti();
      }


  // =========================
  // 🟢 PHASE 1 → POINTS
  // =========================
  await animateCard('.scenario-card:nth-child(1)');
  if (__animationSkipped) return;

  await animateContent('.scenario-card:nth-child(1) .card-content');
  if (__animationSkipped) return;

  await runScoreCount({ withSound }); // 🔥 SOUND SAME
  if (__animationSkipped) return;

      await delay(120);
      if (__animationSkipped) return;

  // =========================
  // ⭐ PHASE 2 → STARS
  // =========================
  await animateCard('.scenario-card:nth-child(2)');
  if (__animationSkipped) return;

  await animateContent('.scenario-card:nth-child(2) .card-content');
  if (__animationSkipped) return;
      await delay(80);
      if (__animationSkipped) return;
  await runStarsSequence({ withSound }); // 🔥 SOUND SAME
  if (__animationSkipped) return;

      await delay(120);
      if (__animationSkipped) return;

  // =========================
  // 🏅 PHASE 3 → BADGE
  // =========================
  await animateCard('.scenario-card:nth-child(3)');
  if (__animationSkipped) return;

  await animateContent('.scenario-card:nth-child(3) .card-content');
  if (__animationSkipped) return;
      await delay(80);
      if (__animationSkipped) return;
  await runStatusReveal({ withSound }); // 🔥 SOUND SAME
  if (__animationSkipped) return;

      await delay(120);
      if (__animationSkipped) return;

  // =========================
  // 📊 PHASE 4 → METER
  // =========================
  await animateCard('.competency-section');
  if (__animationSkipped) return;
      await delay(120);
      if (__animationSkipped) return;
  await runMeterAnimation({ withSound }); // 🔥 SOUND SAME


  // END
  scenarioBody?.classList.remove('run-anim');
  __animationInProgress = false;
}
   

    __audioArmed = true;
    try {
      getAudioContext();
    } catch (e) {}
    setTimeout(() => {
      startSequence({ withSound: true });
    }, 300);

    // Next button handler - skip animation if in progress
    const nextBtn = document.getElementById('nextBtn');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        skipToComplete();
      });
    }

    return () => {};
  }, []);

useEffect(() => {
if (!data) return;

// RESET first
setSequenceStep(0);
setIsSequenceComplete(false);
const t1 = setTimeout(() => setSequenceStep(1), 100); // Points
const t2 = setTimeout(() => setSequenceStep(2), 600); // Stars
const t3 = setTimeout(() => setSequenceStep(3), 1100); // Badge
const t4 = setTimeout(() => {
setSequenceStep(4);
setIsSequenceComplete(true); // ✅ mark complete here
}, 1600);
// Competency

return () => {
clearTimeout(t1);
clearTimeout(t2);
clearTimeout(t3);
clearTimeout(t4);
};
}, [data?.blockId]);
//commented by kaviya
// useEffect(() => {
//   initialized.current = false;
//   animationStarted.current = false;
// }, [sceneriowiseEarnedScore]);
  // by jaswanth
  const {
    scStarsEnabled,
    scCompetencyEnabled,
    scBadgeEnabled,
    scBadgeCriteriaEnabled,
    scBadgeCriteriaScore,
    scMinPassScore,
  } = data || {};


  const { primaryColor } = useColor();
  const bestAllTimeScore = Array.isArray(scenarioAllTimeScore)
    ? Math.max(
        ...scenarioAllTimeScore.filter(
          (s: number) => typeof s === 'number' && s > 0,
        ),
        0,
      )
    : 0;

  // final score used for stars & competency
  const effectiveScoremessage =
    bestAllTimeScore > 0
      ? Math.min(bestAllTimeScore, scenarioTotalScore)
      : sceneriowiseEarnedScore;
  const effectiveScore= sceneriowiseEarnedScore;

  type QuestType = 1 | 2 | 3 | 4;

  const questTitles: Record<QuestType, string> = {
    1: 'Goal Setting',
    2: 'Delegation',
    3: 'Giving Feedback',
    4: 'Conflict Management',
  };
  const questIcons: Partial<Record<QuestType, JSX.Element>> = {
    1: (
      <svg
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        className="neon-icon h-10 w-10 flex-shrink-0"
        style={
          {
            '--main-color': primaryColor,
          } as React.CSSProperties
        }
      >
        <style>
          {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
        </style>

        <defs>
          <clipPath id="clipCircle">
            <circle cx="256" cy="256" r="256" />
          </clipPath>

          <radialGradient id="bg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stop-color="#0b0216" />
            <stop offset="55%" stop-color="#05010b" />
            <stop offset="100%" stop-color="#000000" />
          </radialGradient>

          <radialGradient id="haze" cx="50%" cy="55%" r="55%">
            <stop
              offset="0%"
              stop-color="var(--main-color)"
              stop-opacity="0.25"
            />
            <stop
              offset="55%"
              stop-color="var(--dark-color)"
              stop-opacity="0.10"
            />
            <stop offset="100%" stop-color="#000000" stop-opacity="0" />
          </radialGradient>

          <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
            <stop offset="0%" stop-color="var(--main-color)" />
            <stop offset="100%" stop-color="var(--dark-color)" />
          </linearGradient>

          <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feColorMatrix
              in="b"
              type="matrix"
              values="
           1 0 0 0 0
           0 1 0 0 0
           0 0 1 0 0
           0 0 0 12 -4"
              result="g"
            />
            <feMerge>
              <feMergeNode in="g" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="18"
              result="auraBlur"
            />
            <feColorMatrix
              in="auraBlur"
              type="matrix"
              values="
       1 0 0 0 0
       0 1 0 0 0
       0 0 1 0 0
       0 0 0 4 0"
              result="aura"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="8"
              result="midBlur"
            />
            <feColorMatrix
              in="midBlur"
              type="matrix"
              values="
       1 0 0 0 0
       0 1 0 0 0
       0 0 1 0 0
       0 0 0 2 0"
              result="mid"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="3.2"
              result="hotBlur"
            />
            <feColorMatrix
              in="hotBlur"
              type="matrix"
              values="
       1 0 0 0 0
       0 1 0 0 0
       0 0 1 0 0
       0 0 0 4 0"
              result="hot"
            />

            <feMerge>
              <feMergeNode in="aura" />
              <feMergeNode in="mid" />
              <feMergeNode in="hot" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <radialGradient id="vign" cx="50%" cy="50%" r="65%">
            <stop offset="55%" stop-color="#000" stop-opacity="0" />
            <stop offset="100%" stop-color="#000" stop-opacity="0.55" />
          </radialGradient>
        </defs>

        <g clip-path="url(#clipCircle)">
          <rect width="512" height="512" fill="url(#bg)" />
          <rect width="512" height="512" fill="url(#haze)" />

          <g filter="url(#ringGlow)">
            <circle
              cx="256"
              cy="256"
              r="212"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="6"
              opacity="0.35"
            />
            <circle
              cx="256"
              cy="256"
              r="210"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="3.5"
              opacity="0.75"
            />

            <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="52"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />

            <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="460"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />
          </g>

          <g
            filter="url(#softGlow)"
            transform="translate(256 256) scale(13.5) translate(-12 -12)"
          >
            <path
              d="M21.92,5.62A1,1,0,0,0,21,5H19V3a1,1,0,0,0-.62-.92,1,1,0,0,0-1.09.21l-3,3A1,1,0,0,0,14,6V8.59l-2.21,2.2a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L15.41,10H18a1,1,0,0,0,.71-.29l3-3A1,1,0,0,0,21.92,5.62Z"
              fill="url(#mainGrad)"
            />

            <path
              d="M12,22A10,10,0,0,1,12,2h.42a1,1,0,1,1-.18,2H12a8,8,0,1,0,8,8,1.93,1.93,0,0,0,0-.24,1,1,0,0,1,.91-1.09,1,1,0,0,1,1.09.91c0,.14,0,.28,0,.42A10,10,0,0,1,12,22Zm5.88-8.8a1,1,0,0,0-2-.4A4,4,0,1,1,11.2,8.08a1,1,0,1,0-.4-2,6,6,0,1,0,7.08,7.08Z"
              fill="url(#mainGrad)"
            />
          </g>

          <rect width="512" height="512" fill="url(#vign)" />
        </g>
      </svg>
    ),
    2: (
      <svg
        width="512"
        height="512"
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        className="neon-icon h-10 w-10 flex-shrink-0"
        style={
          {
            '--main-color': primaryColor,
          } as React.CSSProperties
        }
      >
        <style>
          {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
        </style>

        <defs>
          <clipPath id="clipCircle">
            <circle cx="256" cy="256" r="256" />
          </clipPath>

          <radialGradient id="bg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stop-color="#0b0216" />
            <stop offset="55%" stop-color="#05010b" />
            <stop offset="100%" stop-color="#000000" />
          </radialGradient>

          <radialGradient id="haze" cx="50%" cy="55%" r="55%">
            <stop
              offset="0%"
              stop-color="var(--main-color)"
              stop-opacity="0.25"
            />
            <stop
              offset="55%"
              stop-color="var(--dark-color)"
              stop-opacity="0.10"
            />
            <stop offset="100%" stop-color="#000000" stop-opacity="0" />
          </radialGradient>

          <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
            <stop offset="0%" stop-color="var(--main-color)" />
            <stop offset="100%" stop-color="var(--dark-color)" />
          </linearGradient>

          <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feColorMatrix
              in="b"
              type="matrix"
              values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 12 -4"
              result="g"
            />
            <feMerge>
              <feMergeNode in="g" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="18"
              result="auraBlur"
            />
            <feColorMatrix
              in="auraBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0"
              result="aura"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="8"
              result="midBlur"
            />
            <feColorMatrix
              in="midBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0"
              result="mid"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="3.2"
              result="hotBlur"
            />
            <feColorMatrix
              in="hotBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0"
              result="hot"
            />

            <feMerge>
              <feMergeNode in="aura" />
              <feMergeNode in="mid" />
              <feMergeNode in="hot" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <radialGradient id="vign" cx="50%" cy="50%" r="65%">
            <stop offset="55%" stop-color="#000" stop-opacity="0" />
            <stop offset="100%" stop-color="#000" stop-opacity="0.55" />
          </radialGradient>
        </defs>

        <g clip-path="url(#clipCircle)">
          <rect width="512" height="512" fill="url(#bg)" />
          <rect width="512" height="512" fill="url(#haze)" />

          <g filter="url(#ringGlow)">
            <circle
              cx="256"
              cy="256"
              r="212"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="6"
              opacity="0.35"
            />
            <circle
              cx="256"
              cy="256"
              r="210"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="3.5"
              opacity="0.75"
            />

            <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="52"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />

            <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="460"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />
          </g>

          <g
            filter="url(#softGlow)"
            transform="translate(256 256) rotate(90) scale(14) translate(-12 -12)"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M13.803 5.33333C13.803 3.49238 15.3022 2 17.1515 2C19.0008 2 20.5 3.49238 20.5 5.33333C20.5 7.17428 19.0008 8.66667 17.1515 8.66667C16.2177 8.66667 15.3738 8.28596 14.7671 7.67347L10.1317 10.8295C10.1745 11.0425 10.197 11.2625 10.197 11.4872C10.197 11.9322 10.109 12.3576 9.94959 12.7464L15.0323 16.0858C15.6092 15.6161 16.3473 15.3333 17.1515 15.3333C19.0008 15.3333 20.5 16.8257 20.5 18.6667C20.5 20.5076 19.0008 22 17.1515 22C15.3022 22 13.803 20.5076 13.803 18.6667C13.803 18.1845 13.9062 17.7255 14.0917 17.3111L9.05007 13.9987C8.46196 14.5098 7.6916 14.8205 6.84848 14.8205C4.99917 14.8205 3.5 13.3281 3.5 11.4872C3.5 9.64623 4.99917 8.15385 6.84848 8.15385C7.9119 8.15385 8.85853 8.64725 9.47145 9.41518L13.9639 6.35642C13.8594 6.03359 13.803 5.6896 13.803 5.33333Z"
              fill="url(#mainGrad)"
            />
          </g>

          <rect width="512" height="512" fill="url(#vign)" />
        </g>
      </svg>
    ),
    3: (
      <svg
        width="512"
        height="512"
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        className="neon-icon h-10 w-10 flex-shrink-0"
        style={
          {
            '--main-color': primaryColor,
          } as React.CSSProperties
        }
      >
        <style>
          {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
        </style>
        <defs>
          <clipPath id="clipCircle">
            <circle cx="256" cy="256" r="256" />
          </clipPath>

          <radialGradient id="bg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stop-color="#0b0216" />
            <stop offset="55%" stop-color="#05010b" />
            <stop offset="100%" stop-color="#000000" />
          </radialGradient>

          <radialGradient id="haze" cx="50%" cy="55%" r="55%">
            <stop
              offset="0%"
              stop-color="var(--main-color)"
              stop-opacity="0.25"
            />
            <stop
              offset="55%"
              stop-color="var(--dark-color)"
              stop-opacity="0.10"
            />
            <stop offset="100%" stop-color="#000000" stop-opacity="0" />
          </radialGradient>

          <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
            <stop offset="0%" stop-color="var(--main-color)" />
            <stop offset="100%" stop-color="var(--dark-color)" />
          </linearGradient>

          <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feColorMatrix
              in="b"
              type="matrix"
              values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 12 -4"
              result="g"
            />
            <feMerge>
              <feMergeNode in="g" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="ringGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="18"
              result="auraBlur"
            />
            <feColorMatrix
              in="auraBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0"
              result="aura"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="8"
              result="midBlur"
            />
            <feColorMatrix
              in="midBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0"
              result="mid"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="3.2"
              result="hotBlur"
            />
            <feColorMatrix
              in="hotBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0"
              result="hot"
            />

            <feMerge>
              <feMergeNode in="aura" />
              <feMergeNode in="mid" />
              <feMergeNode in="hot" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <radialGradient id="vign" cx="50%" cy="50%" r="65%">
            <stop offset="55%" stop-color="#000" stop-opacity="0" />
            <stop offset="100%" stop-color="#000" stop-opacity="0.55" />
          </radialGradient>
        </defs>

        <g clip-path="url(#clipCircle)">
          <rect width="512" height="512" fill="url(#bg)" />
          <rect width="512" height="512" fill="url(#haze)" />

          <g filter="url(#ringGlow)">
            <circle
              cx="256"
              cy="256"
              r="212"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="6"
              opacity="0.35"
            />
            <circle
              cx="256"
              cy="256"
              r="210"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="3.5"
              opacity="0.75"
            />

            <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="52"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />

            <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="460"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />
          </g>

          <g
            filter="url(#softGlow)"
            transform="translate(256 256) scale(17) translate(-8 -8)"
          >
            <path
              d="m4.75 10.75h-3m12.5-2c0 3-2.79822 5.5-6.25 5.5-3.75 0-6.25-3.5-6.25-3.5v3.5m9.5-9h3m-12.5 2c0-3 2.79822-5.5 6.25-5.5 3.75 0 6.25 3.5 6.25 3.5v-3.5"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </g>

          <rect width="512" height="512" fill="url(#vign)" />
        </g>
      </svg>
    ),
    4: (
      <svg
        width="512"
        height="512"
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        className="neon-icon h-10 w-10 flex-shrink-0"
        style={
          {
            '--main-color': primaryColor,
          } as React.CSSProperties
        }
      >
        <style>
          {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
        </style>
        <defs>
          <clipPath id="clipCircle">
            <circle cx="256" cy="256" r="256" />
          </clipPath>

          <radialGradient id="bg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stop-color="#0b0216" />
            <stop offset="55%" stop-color="#05010b" />
            <stop offset="100%" stop-color="#000000" />
          </radialGradient>

          <radialGradient id="haze" cx="50%" cy="55%" r="55%">
            <stop
              offset="0%"
              stop-color="var(--main-color)"
              stop-opacity="0.25"
            />
            <stop
              offset="55%"
              stop-color="var(--dark-color)"
              stop-opacity="0.10"
            />
            <stop offset="100%" stop-color="#000000" stop-opacity="0" />
          </radialGradient>

          <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
            <stop offset="0%" stop-color="var(--main-color)" />
            <stop offset="100%" stop-color="var(--dark-color)" />
          </linearGradient>

          <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feColorMatrix
              in="b"
              type="matrix"
              values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 9 -4"
              result="g"
            />
            <feMerge>
              <feMergeNode in="g" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="18"
              result="auraBlur"
            />
            <feColorMatrix
              in="auraBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0"
              result="aura"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="8"
              result="midBlur"
            />
            <feColorMatrix
              in="midBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0"
              result="mid"
            />

            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="3.2"
              result="hotBlur"
            />
            <feColorMatrix
              in="hotBlur"
              type="matrix"
              values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0"
              result="hot"
            />

            <feMerge>
              <feMergeNode in="aura" />
              <feMergeNode in="mid" />
              <feMergeNode in="hot" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <radialGradient id="vign" cx="50%" cy="50%" r="65%">
            <stop offset="55%" stop-color="#000" stop-opacity="0" />
            <stop offset="100%" stop-color="#000" stop-opacity="0.55" />
          </radialGradient>
        </defs>

        <g clip-path="url(#clipCircle)">
          <rect width="512" height="512" fill="url(#bg)" />
          <rect width="512" height="512" fill="url(#haze)" />

          <g filter="url(#ringGlow)">
            <circle
              cx="256"
              cy="256"
              r="212"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="6"
              opacity="0.35"
            />
            <circle
              cx="256"
              cy="256"
              r="210"
              fill="none"
              stroke="url(#mainGrad)"
              stroke-width="3.5"
              opacity="0.75"
            />

            <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="52"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />

            <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65" />
            <circle
              cx="256"
              cy="460"
              r="11"
              fill="none"
              stroke="var(--main-color)"
              stroke-width="4"
              opacity="0.9"
            />
          </g>

          <g
            filter="url(#softGlow)"
            transform="translate(256 256) scale(12) translate(-12 -12)"
          >
            <path
              d="M3 11.9914C3 17.6294 7.23896 20.3655 9.89856 21.5273C10.62 21.8424 10.9807 22 12 22V8L3 11V11.9914Z"
              fill="url(#mainGrad)"
            />
            <path
              opacity="0.95"
              d="M14.1014 21.5273C16.761 20.3655 21 17.6294 21 11.9914V11L12 8V22C13.0193 22 13.38 21.8424 14.1014 21.5273Z"
              fill="url(#mainGrad)"
            />
            <path
              opacity="0.95"
              d="M8.83772 2.80472L8.26491 3.00079C5.25832 4.02996 3.75503 4.54454 3.37752 5.08241C3 5.62028 3 7.21907 3 10.4167V11L12 8V2C11.1886 2 10.405 2.26824 8.83772 2.80472Z"
              fill="url(#mainGrad)"
            />
            <path
              d="M15.7351 3.00079L15.1623 2.80472C13.595 2.26824 12.8114 2 12 2V8L21 11V10.4167C21 7.21907 21 5.62028 20.6225 5.08241C20.245 4.54454 18.7417 4.02996 15.7351 3.00079Z"
              fill="url(#mainGrad)"
            />
          </g>

          <rect width="512" height="512" fill="url(#vign)" />
        </g>
      </svg>
    ),
  };

  const title = questTitles[(profile?.currentQuest as QuestType) ?? 1];
  const icon = questIcons[(profile?.currentQuest as QuestType) ?? 1];
  const [minimumScore, setminimumScore] = useState(data.scMinPassScore);

  useEffect(() => {
    setminimumScore(data.scMinPassScore);
  }, [data]);



    const calculateStarFill = (
    earned: number,
    minScore: number | null | undefined,
    totalScore: number
  ): number[] => {
    const stars = [0, 0, 0];
    if (totalScore <= 0) return stars;

   

    const percentage = Math.max(0, Math.min(1, earned / totalScore));

    const totalStarFill = percentage * 3;
  

    stars[0] = normalizeStarValue(Math.min(totalStarFill, 1));
    stars[1] = normalizeStarValue(Math.min(Math.max(totalStarFill - 1, 0), 1));
    stars[2] = normalizeStarValue(Math.min(Math.max(totalStarFill - 2, 0), 1));

 

    return stars;
  };
  
 
const getCongratsMessage = (data: any, scenarioScore: number): string => {
    if (!data) return '';

    const {
      scMinPassEnabled,
      scDistinctionEnabled,
      scCongratsMode,
      scCongratsMessageSingle,
      scCongratsAboveMin,
      scCongratsBelowMin,
      scCongratsAboveDistinction,
      scMinPassScore,
      scDistinctionScore,
      scBadgeCriteriaEnabled,
      scBadgeCriteriaScore,
    } = data;

    const minPassEnabled = scMinPassEnabled === 'true';
    const distinctionEnabled = scDistinctionEnabled === 'true';
    const isScoreWise = scCongratsMode === 'ScoreWise';

    const minScore = Number(scMinPassScore ?? 0);
    const distinctionScore = Number(scDistinctionScore ?? 0);

    // =====================================================
    // RULE 1: ScoreWise OFF → ALWAYS single message
    // =====================================================
    if (!isScoreWise) {
      return scCongratsMessageSingle || '';
    }

    // =====================================================
    // RULE 2: Min Pass ONLY + ScoreWise ON
    // =====================================================
    if (minPassEnabled && !distinctionEnabled) {
      if (scenarioScore < minScore) {
        return scCongratsBelowMin || '';
      }

      if (scenarioScore >= minScore) {
        return scCongratsAboveMin || '';
      }

      return '';
    }

    // =====================================================
    // RULE 3: Distinction ONLY + ScoreWise ON
    // =====================================================
    if (!minPassEnabled && distinctionEnabled) {
      if (scenarioScore >= distinctionScore) {
        return scCongratsAboveDistinction || '';
      }

      // ❌ NO fallback, NO single message
      return '';
    }

    // =====================================================
    // RULE 4: Min Pass + Distinction + ScoreWise ON
    // Priority: Distinction → Min Pass → Below Min
    // =====================================================
    if (minPassEnabled && distinctionEnabled) {
      if (scenarioScore >= distinctionScore) {
        return scCongratsAboveDistinction || '';
      }

      if (scenarioScore >= minScore) {
        return scCongratsAboveMin || '';
      }

      if (scenarioScore < minScore) {
        return scCongratsBelowMin || '';
      }

      return '';
    }

    return '';
  };

  const scenarioScore = sceneriowiseEarnedScore;

  const currentBlockRef = React.useRef<any>(null);

  useEffect(() => {
    if (data) {
      currentBlockRef.current = data;
    }
  }, [data?.blockId]); // or any unique block identifier

//   useEffect(() => {
//     if (
//       typeof sceneriowiseEarnedScore !== 'number' ||
//       !data?.blockScenario ||
//       !learner_game_play_id // gameId
//     )
//       return;
//  if (initialized.current) return;
//   initialized.current = true;
//     const scenarioId =
//       typeof data.blockScenario === 'string'
//         ? parseInt(data.blockScenario.replace(/\D/g, ''), 10)
//         : data.blockScenario;

//     const gameId = String(learner_game_play_id);

//     const stored = JSON.parse(localStorage.getItem('scenarioScores') || '{}');

//     if (!stored[gameId]) {
//       stored[gameId] = {};
//     }

//     stored[gameId][scenarioId] = sceneriowiseEarnedScore;

//     localStorage.setItem('scenarioScores', JSON.stringify(stored));
//   }, [sceneriowiseEarnedScore, data?.blockScenario, learner_game_play_id]);





 useEffect(() => {
    if (
      typeof sceneriowiseHighestEarnedScore !== 'number' ||
      !data?.blockScenario ||
      !learner_game_play_id // gameId
    )
      return;

    const scenarioId =
      typeof data.blockScenario === 'string'
        ? parseInt(data.blockScenario.replace(/\D/g, ''), 10)
        : data.blockScenario;

    // const gameId = String(learner_game_play_id);

    // const stored = JSON.parse(localStorage.getItem('scenarioScores') || '{}');

    // if (!stored[gameId]) {
    //   stored[gameId] = {};
    // }

    // stored[gameId][scenarioId] = sceneriowiseHighestEarnedScore;

    // localStorage.setItem('scenarioScores', JSON.stringify(stored));
    let getscenarioScores = gameProgressData?.scenarioScores || {};

    const questNo = data?.blockQuestNo;
    // const scenarioName = data?.blockScenario;

    if (!getscenarioScores[questNo]) {
      getscenarioScores[questNo] = {};
    }
    getscenarioScores[questNo][scenarioId] = sceneriowiseHighestEarnedScore;


    setGameProgressData((prev: any) => ({
      ...prev,
      scenarioScores: getscenarioScores,
    }));
  }, [sceneriowiseHighestEarnedScore, data?.blockScenario, learner_game_play_id]);



const congratsMessage = getCongratsMessage(data, scenarioScore);

  const competencyPercentage = Math.min(
    Math.round((sceneriowiseEarnedScore / scenarioTotalScore) * 100),
    100,
  );

  const passed = score >= minPassingScore;
  const [animateNote, setAnimateNote] = useState<boolean>(false);
  const earnedScore = sceneriowiseEarnedScore;
  const totalScore = scenarioTotalScore;
  //  const totalScore1 = sceneriowiseHighestEarnedScore;
    const totalScore1 = scenariowisebestscore1;
  const minScore = minimumScore;



  const remainingToFull = Math.max(totalScore - totalScore1, 0);

  const isBelowMin = totalScore1 < minScore;
  const isPerfectScore = totalScore1 === totalScore;
  const remainingToMin = Math.max(minScore - earnedScore, 0);
 

  // useEffect(() => {
  //   if (data?.scLeaderboardEnabled === 'true' && data?.blockScenario) {
  //     AllLearnerLearderboard();
  //   }
  // }, [data?.blockScenario]);

  const badgeCriteriaScore = Number(scBadgeCriteriaScore || 0);

  const isBadgeMissed =
    scBadgeEnabled === 'true' &&
    scBadgeCriteriaEnabled === 'true' &&
    sceneriowiseEarnedScore < badgeCriteriaScore;
 
  const showBadge =
    scBadgeEnabled === 'true' &&
    (scBadgeCriteriaEnabled !== 'true' ||
      sceneriowiseEarnedScore >= badgeCriteriaScore);

  const getNoteNextData = () => {
    setAnimateNote(true);

    const currentBlock = currentBlockRef.current;

    if (currentBlock?.scLeaderboardEnabled === 'true') {
      // AllLearnerLearderboard();
      BlockSeqNextPreviousNotes(data?.blockSecondaryId, data?.blockQuestNo)
                  const blockdata ={
            blockId: currentBlock.blockId,
            blockGameId: currentBlock.blockGameId,
            blockQuestNo: currentBlock.blockQuestNo,
            blockScenario: currentBlock.blockScenario,
            blockSecondaryId: currentBlock.blockSecondaryId,
            blockPrimarySequence: currentBlock.blockPrimarySequence,
            blockDragSequence: currentBlock.blockDragSequence,
            blockShowNavigate: currentBlock.blockShowNavigate,
            blockLeadTo: currentBlock.blockLeadTo,
            blockChoosen: currentBlock.blockChoosen,
            storyscreentype:"scenarioLeaderboard"
          }
      setTimeout(() => {
        getNoteNextDataone?.();
        setlearnerActivityrec((prev:any)=>
            createOrUpdateStoryBlock(
              prev,
              {
                ...blockdata,
                scenarioleaderboard:"showing"
              },
              deviceInfo
            )
          );
      }, 500);
    } else {
      getData(currentBlock);
    }
  };

  const [showReplayPopup, setShowReplayPopup] = useState(false);

  // Next arrow click
  const [isExitingScenario, setIsExitingScenario] = useState(false);

  const handleNextClick = () => {
    setShowScenarioLeaderboard(true);
    setIsExitingScenario(true);
    const currentBlock = currentBlockRef.current;

    if (currentBlock?.scLeaderboardEnabled === 'false') {
      setShowReplayPopup(true);
        const blockdata = {
        blockId: data.blockId,
        blockGameId: data.blockGameId,
        blockQuestNo: data.blockQuestNo,
        blockScenario: data.blockScenario,
        blockSecondaryId: data.blockSecondaryId,
        blockPrimarySequence: data.blockPrimarySequence,
        blockDragSequence: data.blockDragSequence,
        blockShowNavigate: data.blockShowNavigate,
        blockLeadTo: data.blockLeadTo,
        blockChoosen: data.blockChoosen,
      }
      setlearnerActivityrec((prev:any)=>
        createOrUpdateStoryBlock(
          prev,
          {
            ...blockdata,
            replyPromptopen:true,
            replyPromptclose:false,
            promptscenario : isBelowMin ? 'mandatoryReplay' : 'optionalReplay',
            replayplayaction : false
          },
          deviceInfo
        )
      );
    } else {
      getNoteNextData();
    }
  };

  // Replay from popup

  const handlePopupNext = () => {
    setShowReplayPopup(false);
        const blockdata = {
        blockId: data.blockId,
        blockGameId: data.blockGameId,
        blockQuestNo: data.blockQuestNo,
        blockScenario: data.blockScenario,
        blockSecondaryId: data.blockSecondaryId,
        blockPrimarySequence: data.blockPrimarySequence,
        blockDragSequence: data.blockDragSequence,
        blockShowNavigate: data.blockShowNavigate,
        blockLeadTo: data.blockLeadTo,
        blockChoosen: data.blockChoosen,
      }
      setlearnerActivityrec((prev:any)=>
        createOrUpdateStoryBlock(
          prev,
          {
            ...blockdata,
            replyPromptclose:true,
            promptscenario : isBelowMin ? 'mandatoryReplay' : 'optionalReplay',
            replayplayaction : false
          },
          deviceInfo
        )
      );
    getNoteNextData(); // existing next flow
  };

  const getPopupMessage = () => {
    if (isBelowMin) {
      return `You need to earn ${minScore - totalScore1
        } more points to reach the minimum score. Replay is required.`;
    }

    if (isPerfectScore) {
      return `Would you like to replay this scenario?`;
    }

    return `You’re almost there! Earn ${remainingToFull} more points to achieve a perfect score.`;
  };

  if (isExitingScenario) {
    return null;
  }

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
     .stars {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 20px;
    }

    .scenario-aura-wrapper {
      position: relative;
      width: 28px;
      height: 28px;
    }

    .starburst {
      position: relative;
      width: 34px;
      height: 34px;
      z-index: 1;
    }

    .starburst svg {
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
    }

    .star-empty .star-body {
      fill: transparent;
      stroke: rgba(${hexToRgb(primaryColor)}, 0.35);
      stroke-width: 1.5;
    }

    .star-fill .star-body {
      fill: ${primaryColor};
      stroke: ${primaryColor};
      stroke-width: 1;
    }

    .star-glow { fill: #fff; }

    .starburst.filled .star-fill {
      filter: drop-shadow(0 0 8px ${primaryColor});
      animation: starPulse 2s ease-in-out infinite;
    }

    .starburst .star-fill {
      opacity: 1;
      clip-path: inset(0 calc(100% - var(--fillPct, 0%)) 0 0);
      transition: clip-path 280ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes starPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    .aura-ring {
      position: absolute;
      width: 44px;
      height: 44px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;
    }

    .aura-ring circle {
      fill: none;
      stroke: ${primaryColor};
      stroke-width: 1.1;
      stroke-dasharray: 8 12;
    }

    .starburst.filled + .aura-ring { opacity: 0.7; }

    .star-pulse {
      position: absolute;
      left: 50%; top: 50%;
      width: 54px; height: 54px;
      border-radius: 999px;
      border: 1px solid ${primaryColor};
      transform: translate(-50%, -50%) scale(0.55);
      opacity: 0;
      pointer-events: none;
      filter: drop-shadow(0 0 10px ${primaryColor});
      animation: pulseRing 560ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    @keyframes pulseRing {
      0% { opacity: 0; transform: translate(-50%, -50%) scale(0.55); }
      22% { opacity: calc(var(--pulseAlpha, 1) * 0.85); }
      100% { opacity: 0; transform: translate(-50%, -50%) scale(1.55); }
    }

    .scenario-body.run-anim .stars .starburst {
      opacity: 0;
      transform: scale(0.65);
      filter: blur(8px);
    }

    .scenario-body.run-anim .stars .starburst.reveal {
      animation: starPop 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    @keyframes starPop {
      0%   { opacity: 0; transform: scale(0.65); filter: blur(8px); }
      55%  { opacity: 1; transform: scale(1.18); filter: blur(0); }
      100% { opacity: 1; transform: scale(1);    filter: blur(0); }
    }

    .scenario-body.run-anim .aura-ring { opacity: 0; }

    .scenario-body.run-anim .aura-ring.ring-on {
      animation: ringIn 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    @keyframes ringIn {
      0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.72); filter: blur(6px); }
      100% { opacity: 0.7; transform: translate(-50%, -50%) scale(1); filter: blur(0); }
    }

    .scene-flash {
      position: fixed; inset: 0;
      pointer-events: none; z-index: 200;
      opacity: 0; background: rgba(255,255,255,0.15);
    }

    .scenario-body.flash-on .scene-flash {
      animation: sceneFlash 180ms ease-out forwards;
    }

    @keyframes sceneFlash {
      0%   { opacity: 0; }
      35%  { opacity: var(--flashAlpha, 0.32); }
      100% { opacity: 0; }
    }

      `,
        }}
      />
      <div className="scenario-body">
        <div className="scene-flash" aria-hidden="true"></div>

        <div className="border-animation-wrapper">
          <div className="border-animation-inner"></div>

          <div className="scenario-panel self-stretch">
            {/* <div
             className="min-h-[100dvh] flex items-center justify-center px-4 py-6"
             style={{ background: ` linear-gradient( 180deg, rgba(${hexToRgb(primaryColor)}, 0.25), rgba(${hexToRgb(primaryColor)}, 0.08) ) `, }}
            > */}
            `,
            <div className="hud-bracket tl"></div>
            <div className="hud-bracket tr"></div>
            <div className="hud-bracket bl"></div>
            <div className="hud-bracket br"></div>
          
            <div className="scenario-title">Scenario Complete</div>
            <p className="message-box">
              {congratsMessage}
              
            </p>
           
            <div className="summary-grid">
              {sequenceStep >= 1 && (
              <div className="scenario-card">
                <span className="scenario-card-label">Points</span>
                <div className="card-content">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="30"
                    height="30"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="trophy-icon"
                    style={{ display: 'block' }}
                  >
                    <defs>
                      <filter
                        id="scoreIconFilter"
                        x="-2.65"
                        y="-1.86"
                        width="29.26"
                        height="29.26"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset />
                        <feGaussianBlur stdDeviation="3.33" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                        />
                        <feBlend
                          mode="normal"
                          in2="BackgroundImageFix"
                          result="effect1_dropShadow"
                        />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="effect1_dropShadow"
                          result="shape"
                        />
                      </filter>
                    </defs>
                    <g filter="url(#scoreIconFilter)">
                      <path
                        d="M15.15 7.64L14.38 8.99H9.96L9.19 7.64C8.88 7.20 9.17 6.64 9.69 6.64H14.84C15.36 6.64 15.65 7.20 15.15 7.64Z"
                        fill={primaryColor}
                      />
                      <path
                        opacity="0.4"
                        d="M17.39 14.53C17.39 15.84 16.74 18.47 12.14 18.47C7.54 18.47 6.88 15.84 6.88 14.53C6.88 11.90 8.85 9.93 9.93 8.99H14.35C15.42 9.93 17.39 11.90 17.39 14.53Z"
                        fill={primaryColor}
                      />
                      <path
                        d="M13.78 14.93C13.78 14.26 13.38 13.67 12.67 13.50L11.84 13.29C11.74 13.26 11.65 13.20 11.58 13.11C11.51 13.03 11.48 12.92 11.48 12.81C11.48 12.54 11.69 12.32 11.96 12.32H12.32C12.56 12.32 12.76 12.51 12.79 12.76C12.82 13.03 13.04 13.22 13.31 13.19C13.58 13.16 13.80 12.95 13.77 12.65C13.70 12.08 13.23 11.65 12.68 11.57V11.44C12.68 11.19 12.49 11.00 12.24 11.00C11.99 11.00 11.80 11.19 11.80 11.44V11.57C11.24 11.66 10.74 12.11 10.74 12.81C10.74 13.14 10.85 13.46 11.05 13.71C11.25 13.98 11.54 14.12 11.86 14.19L12.69 14.40C12.89 14.45 13.04 14.66 13.04 14.93C13.04 15.06 13.00 15.18 12.92 15.28C12.85 15.38 12.75 15.43 12.64 15.43H12.28C12.04 15.43 11.84 15.24 11.81 14.99C11.78 14.72 11.56 14.53 11.29 14.56C11.02 14.59 10.80 14.80 10.83 15.10C10.90 15.67 11.37 16.10 11.92 16.18V16.31C11.92 16.56 12.11 16.75 12.36 16.75C12.61 16.75 12.80 16.56 12.80 16.31V16.18C13.08 16.13 13.33 16.00 13.52 15.79C13.79 15.52 13.94 15.15 13.94 14.76L13.78 14.93Z"
                        fill={primaryColor}
                      />
                    </g>
                  </svg>

                  <span
                    className="score-value"
                    data-target={sceneriowiseEarnedScore}
                    data-max={scenarioTotalScore}
                  >
                  {/* {sceneriowiseEarnedScore} */}
                  0/{scenarioTotalScore}
                  </span>
                </div>
              </div>
              )}
              {/* <p className="mt-2 text-3xl font-semibold text-white"> </p> */}

              {/* <div
                className="scenario-stat-card flex flex-col items-center justify-center text-center"
                style={{
                  height: '122px',
                  background: 'rgba(0,0,0,0.25)',
                  border: `1px solid rgba(${hexToRgb(primaryColor)},0.35)`,
                  ['--glow-color' as any]: hexToRgb(primaryColor),
                }}
              > */}
{sequenceStep >= 2 && (
              <div className="scenario-card">
                {scStarsEnabled === 'true' && (
                  <StarsCard
                    filledStars={calculateStarFill(
                      sceneriowiseEarnedScore,
                      minimumScore,
                      scenarioTotalScore,
                    )}
                    totalStars={3}
                    visible
                    animationDelay={400}
                  />
                )}
              </div>
)}
              {/* BADGE */}
            

               { sequenceStep >= 3 && scBadgeEnabled === 'true' && (
  <div className="scenario-card">
    
    {/* Label */}
    <div className="scenario-card-label">
      {showBadge ? badgename || 'Badge Earned' : 'Reward'}
    </div>

    {/* Content */}
    <div className="card-content">

      {showBadge ? (
        <img
          src={`${API_SERVER}/${
            badgeImage || `${API_SERVER}/default_badge.png`
          }`}
          alt={badgeImage}
          className="scenario-status-badge-img h-32 w-32"
          // className="w-18  h-20 object-contain text-xl font-semibold text-white"
          
        />
      ) : isBadgeMissed ? (
        <div className="no-badge-text text-center text-white">
          You missed the reward!
        </div>
      ) : null
      
      //  ( <div className="no-badge-text text-center text-white">
      //     No badge available
      //   </div>
      // )

      }

    </div>
  </div>
)}
            </div>
            {/* XP */}
            {/* {scBadgeEnabled === "true" && (

             

               <div
                  className="scenario-stat-card flex flex-col items-center justify-center text-center"
                  style={{
                    height: '122px',
                    background: 'rgba(0,0,0,0.25)',
                    border: `1px solid rgba(${hexToRgb(primaryColor)},0.35)`,
                    ['--glow-color' as any]: hexToRgb(primaryColor),
                  }}
                >


                  <p className="text-sm font-bold tracking-widest  uppercase" style={{ color: primaryColor }}>
                    {badgename || 'Badge Earned'}
                  </p>

                  <img
                       src={`${API_SERVER}/${badgeImage || `${API_SERVER}/default_badge.png`}`}
                        alt={badgeImage}
                         className="w-18  h-20 object-contain text-xl font-semibold text-white"
                       />
                </div>
            )} */}
            {/*  new place for competeny meter. */}
            {/* NEURAL SYNC (UNDER STARS, SMALL) */}
            {sequenceStep >= 4 && scCompetencyEnabled === 'true' && (
              <>
                {/* <div
                className="
                mt-8
                w-full
                bg-black/60
                px-4 py-5
                shadow-[0_0_30px_rgba(0,255,255,0.15)]
              "
                style={{
                  border: `1px solid rgba(${hexToRgb(primaryColor)},0.4)`,
                  boxShadow: `0 0 30px rgba(${hexToRgb(primaryColor)},0.25)`
                }}
              > */}

                {/* LABEL ROW */}
                {/* <div className="flex items-center justify-between mb-2 px-1"> */}
                <div className="competency-section">
                  <span
                    className="mb-1 mt-1 text-sm font-bold uppercase tracking-[0.28em]"
                    style={{ color: primaryColor }}
                  >
                    {icon}
                  </span>
                  {/* <span className="text-sm font-bold uppercase tracking-[0.28em] mt-1 mb-1" style={{ color: primaryColor }}> */}
                  {/* Goal Setting */}
                  {/* <div className="flex items-center gap-3">
                    <span className="text-sm font-bold uppercase tracking-[0.28em] mt-1 mb-1" style={{ color: primaryColor }}>
                      
                      {title}
                    </span>
                  </div>

                <span className="text-sm uppercase tracking-widest" style={{ color: primaryColor }}>
                  {competencyPercentage}%
                  </span> */}
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        className="scenario-card-label text-[16px]"
                        style={{ border: 'none', margin: 0, padding: 0 }}
                      >
                        {title}
                      </span>
                      <span className="meter-percentage ">
                        {competencyPercentage}%
                      </span>
                    </div>
                    {/* <div className="meter-bar"><div className="meter-fill"></div></div> */}
                    {/* <div
                      className="relative h-[4px] overflow-hidden meter-bar"
                      style={{
                        background: `rgba(${hexToRgb(primaryColor)}, 0.15)`,
                        border: `1px solid rgba(${hexToRgb(primaryColor)}, 0.35)`
                      }}
                    >

                      <motion.div
                        className="absolute left-0 top-0 bottom-0 meter-fill"
                        initial={{ width: '0%' }}
                        animate={{ width: `${competencyPercentage}%` }}
                        // animate={{ width: `${50}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        style={{
                          background: `linear-gradient( 90deg, rgba(${hexToRgb(primaryColor)},0.6), rgba(${hexToRgb(primaryColor)},1) )`,
                          boxShadow: `0 0 8px rgba(${hexToRgb(primaryColor)}, 0.85)`
                        }}
                      />
                    </div> */}

                    <div className="meter-bar">
                      <div
                        className="meter-fill"
                        data-pct={competencyPercentage}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="pt-6">
              {isSequenceComplete && (
              <div className="flex w-full items-center justify-between">
                <NavigationArrow
                  direction="left"
                  onClick={() => {
                    playMenuSound()
                    LastModiPrevData(data)}
                  }
                />

                <NavigationArrow direction="right" onClick={() => {
                    playMenuSound()
                      if (data?.scLeaderboardEnabled === 'true') {
      //  AllLearnerLearderboard();
    }
                    handleNextClick()
                  }
                  } />
              </div>
              )}
            </div>
          </div>
          {/* </div> */}

          {data.scLeaderboardEnabled === 'false' && showReplayPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="relative w-full max-w-lg overflow-hidden rounded-3xl p-6 backdrop-blur-[2.5rem]"
              >
                <div className="relative flex flex-col items-center justify-end gap-8">
                  {/* Main Content */}
                  <div className="flex w-full flex-col gap-6">
                    {/* Title */}
                    <div
                      className="text-center text-4xl font-normal tracking-[0.005rem]"
                      style={{
                        color: primaryColor,
                        textShadow: `rgba(${hexToRgb(
                          primaryColor,
                        )},0.5) 0px 0px 9px`,
                      }}
                    >
                      <p>Scenario Review</p>
                    </div>

                    {/* Decorative Line */}
                    <div className="relative flex h-[1px] w-full items-center justify-center">
                      <div className="w-full rotate-[179.99deg] scale-y-[-1]">
                        <svg
                          className="block h-[1px] w-full"
                          fill="none"
                          preserveAspectRatio="none"
                          viewBox="0 0 525 1"
                        >
                          <line
                            stroke={`url(#paint0_linear_review_${primaryColor.replace(
                              '#',
                              '',
                            )})`}
                            strokeWidth="0.894962"
                            x2="524.592"
                            y1="0.552519"
                            y2="0.552519"
                          />
                          <defs>
                            <linearGradient
                              gradientUnits="userSpaceOnUse"
                              id={`paint0_linear_review_${primaryColor.replace(
                                '#',
                                '',
                              )}`}
                              x1="524.592"
                              x2="0"
                              y1="0.999899"
                              y2="0.999919"
                            >
                              <stop stopColor={primaryColor} stopOpacity="0" />
                              <stop
                                offset="0.484375"
                                stopColor={primaryColor}
                                stopOpacity="0.994792"
                              />
                              <stop
                                stopColor={primaryColor}
                                stopOpacity="0"
                                offset="1"
                              />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                    </div>

                    {/* Content Text */}
                    <div className="font-rubik z-10 max-h-44 overflow-auto text-center text-[1.188rem] font-normal leading-[2.125rem] -tracking-[0.005rem] text-white">
                      {getPopupMessage()}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="relative z-10 flex w-full gap-4">
                    <Buttons
                      onClick={() =>{
                        handleScenarioReplayButtonClick(
                          isBelowMin ? 'mandatoryReplay' : 'optionalReplay',
                          data.blockScenario,
                        );
                       const blockdata = {
                            blockId: data.blockId,
                            blockGameId: data.blockGameId,
                            blockQuestNo: data.blockQuestNo,
                            blockScenario: data.blockScenario,
                            blockSecondaryId: data.blockSecondaryId,
                            blockPrimarySequence: data.blockPrimarySequence,
                            blockDragSequence: data.blockDragSequence,
                            blockShowNavigate: data.blockShowNavigate,
                            blockLeadTo: data.blockLeadTo,
                            blockChoosen: data.blockChoosen,
                          }
                          setlearnerActivityrec((prev:any)=>
                            createOrUpdateStoryBlock(
                              prev,
                              {
                                ...blockdata,
                                replyPromptclose:true,
                                promptscenario : isBelowMin ? 'mandatoryReplay' : 'optionalReplay',
                                replayplayaction : true
                              },
                              deviceInfo
                            )
                          );
                      
                      }
                      }
                      variant="ghost"
                      size="sm"
                      className="font-rubik h-12 flex-1 rounded-[2.5rem] px-4 py-2 text-lg font-medium tracking-[0.034rem] text-white transition-all duration-200 hover:scale-110"
                      style={{
                        background: `linear-gradient(100deg, rgba(${hexToRgb(
                          primaryColor,
                        )}) -27.25%, rgba(0, 0, 0, 0.8) 127.803%)`,
                        border: `1px solid rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.5)`,
                        boxShadow: `0px 0px 21.786px rgba(${hexToRgb(
                          primaryColor,
                        )}, 0.4)`,
                      }}
                    >
                      Replay
                    </Buttons>

                    {!isBelowMin && (
                      <Buttons
                        onClick={handlePopupNext}
                        variant="ghost"
                        size="sm"
                        className="font-rubik h-12 flex-1 rounded-[2.5rem] px-4 py-2 text-lg font-medium tracking-[0.034rem] text-white transition-all duration-200 hover:scale-110"
                        style={{
                          background: `rgba(${hexToRgb(primaryColor)}, 0.15)`,
                          border: `1px solid rgba(${hexToRgb(
                            primaryColor,
                          )}, 0.35)`,
                          boxShadow: `0px 0px 16px rgba(${hexToRgb(
                            primaryColor,
                          )}, 0.25)`,
                        }}
                      >
                        Next
                      </Buttons>
                    )}
                  </div>

                  {/* Background Ellipse Glow */}
                  <div className="absolute left-[18rem] h-[24rem] w-[26rem] mix-blend-lighten">
                    <div className="absolute inset-[-120%]">
                      <svg
                        className="size-full"
                        fill="none"
                        viewBox="0 0 1250 1210"
                      >
                        <g opacity="0.2">
                          <ellipse
                            cx="624.898"
                            cy="604.747"
                            rx="218.898"
                            ry="198.747"
                            fill={primaryColor}
                          />
                        </g>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Card Inner Shadow */}
                <div className="pointer-events-none absolute inset-0 shadow-[1.58049px_3.16098px_12.6439px_0px_inset_rgba(248,248,248,0.06)]" />

                {/* Card Border */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-3xl border"
                  style={{
                    border: `1.18537px solid ${primaryColor}33`,
                    boxShadow: `0px 3.16098px 3.16098px rgba(${hexToRgb(
                      primaryColor,
                    )}, 0.12)`,
                  }}
                />
              </motion.div>
            </div>
          )}

          {/* </motion.div> */}
          {/* </div> */}
          {/* </div> */}

          {/* </div> */}
        </div>
      </div>
    </>
  );
};

export default ScenarioCompletionFutureTheme;
